import { errorBodyHas } from "@base/modules/patientSpecialPackage/packageDetailRules";
import { vnHour } from "./bookingCommon";

const SLOT_TAKEN = "appointment already";
const APPOINTMENT_EXISTED = ["Appointment Existed", "Appointment order existed"];
const START_TIME_IN_PAST = "Start Time must greater than or equal now";
const HALF_DAY_QUOTA_REACHED = "HALF_DAY_QUOTA_REACHED";

export interface IGroupExamErrorContext {
  /** `{lang}_noti_half_day_quota_reached` of the package, else the app's own sentence. */
  halfDayTemplate: string;
  /** `half_day_session_quota_check_from`, e.g. "50". */
  quotaPercent?: string;
}

export interface IBookingErrorContext {
  translate: (key: string, options?: Record<string, unknown>) => string;
  hotline?: string;
  /** Set on a group checkup, which has its own rejections. */
  groupExam?: IGroupExamErrorContext;
  /** The chosen slot's start, to tell a morning from an afternoon. */
  periodStartTime?: unknown;
  /** `yyyy-MM-dd` */
  appointmentDate?: string;
}

/** `reloadSlots`: the slot grid is stale after this rejection and has to be fetched again. */
export type IBookingErrorResult = { kind: "message"; text: string; reloadSlots: boolean } | { kind: "generic" };

/** The message of a rejected booking - the body may be `{message}`, `{error: {message}}` or a bare string. */
export const bookingErrorMessage = (body: unknown): string => {
  if (typeof body === "string") {
    return body;
  }

  if (!body || typeof body !== "object") {
    return "";
  }

  const record = body as Record<string, unknown>;
  const nested = record.error && typeof record.error === "object" ? (record.error as Record<string, unknown>).message : undefined;

  return [record.message, nested, record.detail].find((value): value is string => typeof value === "string") ?? "";
};

/**
 * Fills a "quota reached" template. Placeholders are numbered (`&s1`/`%s1`, the backend uses `&`)
 * or bare `%s`: three or more of them read [quota, half day, date], fewer read [half day, date].
 */
export const fillHalfDayTemplate = (template: string, values: { quota: string; session: string; date: string }) => {
  const numbered = Array.from(template.matchAll(/[%&]s(\d+)/g)).map((match) => Number(match[1]));
  const count = numbered.length ? Math.max(...numbered) : (template.match(/%s/g) ?? []).length;
  const ordered = count >= 3 ? [values.quota, values.session, values.date] : [values.session, values.date];
  let text = template;

  if (numbered.length) {
    for (let index = count; index >= 1; index--) {
      const value = ordered[index - 1] ?? "";

      text = text.split(`%s${index}`).join(value).split(`&s${index}`).join(value);
    }
  } else {
    ordered.forEach((value) => {
      text = text.replace("%s", value);
    });
  }

  // A literal "\n" and a "/n" that does not sit right after a letter ("giờ/ngày" must survive).
  return text.replace(/\\n/g, "\n").replace(/(^|[^\p{L}])\/n/gu, "$1\n");
};

/** What the patient is told when a booking is refused (regular §12.2, package §12.15). */
export const describeBookingError = (body: unknown, context: IBookingErrorContext): IBookingErrorResult => {
  const { translate } = context;
  const message = bookingErrorMessage(body);

  if (message.includes(SLOT_TAKEN)) {
    return { kind: "message", text: translate("booking.errors.slotTaken"), reloadSlots: true };
  }

  if (APPOINTMENT_EXISTED.some((needle) => message.includes(needle))) {
    return { kind: "message", text: translate("booking.errors.appointmentExisted", { hotline: context.hotline ?? "" }), reloadSlots: true };
  }

  if (message.includes(START_TIME_IN_PAST)) {
    return { kind: "message", text: translate("booking.errors.startTimeInPast"), reloadSlots: true };
  }

  if (context.groupExam) {
    if (errorBodyHas(body, HALF_DAY_QUOTA_REACHED)) {
      const hour = vnHour(context.periodStartTime);
      const date = context.appointmentDate ? context.appointmentDate.split("-").reverse().join("/") : "";
      const quota = context.groupExam.quotaPercent?.trim() ? `${context.groupExam.quotaPercent.trim()}%` : "";
      const session = Number.isFinite(hour) && hour < 12 ? translate("booking.morning") : translate("booking.afternoon");

      return { kind: "message", text: fillHalfDayTemplate(context.groupExam.halfDayTemplate, { quota, session, date }), reloadSlots: true };
    }

    // A group checkup's other refusals are worded by the backend for the patient.
    return message.trim() ? { kind: "message", text: message.trim(), reloadSlots: true } : { kind: "generic" };
  }

  return { kind: "generic" };
};
